import Box from "@mui/material/Box";
import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import { useGetPublicReportsQuery } from "../../services/report.query";
import { DefaultPage } from "../../layouts/default-page";
import {
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { RowActions } from "../../components/common/RowActions";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { StatusChip } from "../../components/common/StatusChip";
import { useTableState } from "../../hooks/useTableState";
import { MRT_ColumnDef } from "material-react-table";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { Notify } from "../../layouts/notify";

export default function PublicReport() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: PublicReports,
    isLoading,
    isFetching,
    isError,
  } = useGetPublicReportsQuery("");

  const rowsPerPage = 10;

  const defaultFilters = useMemo(
    () => ({
      startDate: "",
      endDate: "",
      status: "ALL",
      citizenSearch: "",
      titleSearch: "",
      venueSearch: "",
      search: "",
    }),
    []
  );

  // Persistent URL Table State: saves and restores page, search query, and pagination across route transitions
  const {
    page,
    pageSize,
    filters,
    hasActiveFilters,
    setPage,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: rowsPerPage,
  });

  // Local draft filter inputs
  const [draftStartDate, setDraftStartDate] = useState(filters.startDate || "");
  const [draftEndDate, setDraftEndDate] = useState(filters.endDate || "");
  const [draftStatus, setDraftStatus] = useState(filters.status || "ALL");
  const [draftCitizenSearch, setDraftCitizenSearch] = useState(filters.citizenSearch || "");
  const [draftTitleSearch, setDraftTitleSearch] = useState(filters.titleSearch || "");
  const [draftVenueSearch, setDraftVenueSearch] = useState(filters.venueSearch || "");
  const [draftSearch, setDraftSearch] = useState(filters.search || "");

  // Export overlay state (disables platform and displays progress)
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  // Sync draft states when URL filters change (e.g. Back button or external navigation)
  useEffect(() => {
    setDraftStartDate(filters.startDate || "");
    setDraftEndDate(filters.endDate || "");
    setDraftStatus(filters.status || "ALL");
    setDraftCitizenSearch(filters.citizenSearch || "");
    setDraftTitleSearch(filters.titleSearch || "");
    setDraftVenueSearch(filters.venueSearch || "");
    setDraftSearch(filters.search || "");
  }, [filters]);

  // Save current table URL in sessionStorage for return navigation
  useEffect(() => {
    sessionStorage.setItem("last_public_reports_url", `${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  // Navigate to details page while passing current returnUrl to preserve table state
  const handleViewDetails = (report: any) => {
    sessionStorage.setItem("last_public_reports_url", `${location.pathname}${location.search}`);
    navigate(`/public-reports/details?id=${report.id}`, {
      state: {
        report,
        returnUrl: `${location.pathname}${location.search}`,
      },
    });
  };

  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "number",
      header: "No.",
      size: 65,
      enableSorting: false,
      Cell: ({ row }: any) => (
        <Typography sx={{ fontSize: 13, color: "#64748B" }}>
          {(page - 1) * pageSize + row.index + 1}
        </Typography>
      ),
    },
    {
      accessorKey: "user",
      header: "User",
      size: 200,
      Cell: ({ row }: any) => {
        const u = row.original.user;
        const profilePic =
          u?.profile_picture ||
          "https://smrgejlacnpeerpzxezv.supabase.co/storage/v1/object/public/assets/default-user-image.png";

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              component="img"
              src={profilePic}
              sx={{
                width: 32,
                height: 32,
                cursor: "pointer",
                borderRadius: "50%",
                border: "1px solid #E2E8F0",
              }}
              onClick={(e) => {
                e.stopPropagation();
                window.open(profilePic, "_blank");
              }}
              alt="User"
            />
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
              {u?.name} {u?.last_name}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "title",
      header: "Content Title",
      size: 200,
      Cell: ({ cell }) => (
        <Typography sx={{ fontSize: 13, color: "#334155" }}>
          {(cell.getValue() as string) || "—"}
        </Typography>
      ),
    },
    {
      accessorKey: "name",
      header: "Venue Name",
      size: 180,
      Cell: ({ cell }) => (
        <Typography sx={{ fontSize: 13, color: "#334155" }}>
          {(cell.getValue() as string) || "—"}
        </Typography>
      ),
    },
    {
      accessorKey: "event_datetime",
      header: "Event Date",
      size: 210,
      Cell: ({ cell }: any) => (
        <Typography sx={{ fontSize: 13, color: "#334155" }}>
          {cell.getValue()
            ? format(new Date(cell.getValue()), "MMM dd, yyyy · hh:mm a")
            : "—"}
        </Typography>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 140,
      Cell: ({ row }: any) => {
        const status = row.original.status || "PENDING";
        return <StatusChip label={status} />;
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 90,
      enableSorting: false,
      Cell: ({ row }: any) => (
        <RowActions
          actions={[
            {
              label: "View",
              onClick: () => handleViewDetails(row.original),
            },
          ]}
        />
      ),
    },
  ];

  // In-memory multi-field filtering across all public reports
  const filteredData = useMemo(() => {
    if (!PublicReports) return [];

    return PublicReports.filter((item: any) => {
      // 1. Status Filter
      if (filters.status && filters.status !== "ALL") {
        const itemStatus = (item.status || "PENDING").toUpperCase();
        if (itemStatus !== filters.status.toUpperCase()) return false;
      }

      // 2. Date Filters (evaluates event_datetime or created_at)
      if (filters.startDate) {
        const dateStr = item.event_datetime || item.created_at;
        if (!dateStr) return false;
        const itemDate = new Date(dateStr);
        const start = new Date(`${filters.startDate}T00:00:00`);
        if (itemDate < start) return false;
      }
      if (filters.endDate) {
        const dateStr = item.event_datetime || item.created_at;
        if (!dateStr) return false;
        const itemDate = new Date(dateStr);
        const end = new Date(`${filters.endDate}T23:59:59`);
        if (itemDate > end) return false;
      }

      // 3. Citizen Name Search
      if (filters.citizenSearch) {
        const q = filters.citizenSearch.trim().toLowerCase();
        const fullName = `${item.user?.name || ""} ${item.user?.last_name || ""}`.toLowerCase();
        const email = (item.user?.email || "").toLowerCase();
        if (!fullName.includes(q) && !email.includes(q)) return false;
      }

      // 4. Content Title Search
      if (filters.titleSearch) {
        const q = filters.titleSearch.trim().toLowerCase();
        const title = (item.title || "").toLowerCase();
        if (!title.includes(q)) return false;
      }

      // 5. Venue Name Search
      if (filters.venueSearch) {
        const q = filters.venueSearch.trim().toLowerCase();
        const venue = (item.name || "").toLowerCase();
        if (!venue.includes(q)) return false;
      }

      // 6. Universal Keyword / Report ID Search
      if (filters.search) {
        const q = filters.search.trim().toLowerCase();
        const searchContent = `
          ${item.user?.name || ""} 
          ${item.user?.last_name || ""} 
          ${item.user?.email || ""}
          ${item.title || ""}
          ${item.name || ""}
          ${item.id || ""}
          ${item.status || ""}
          ${item.report_description || ""}
        `.toLowerCase();
        if (!searchContent.includes(q)) return false;
      }

      return true;
    });
  }, [filters, PublicReports]);

  // Paginated chunk for active view
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, page, pageSize]);

  const handleSearch = () => {
    setFilters({
      startDate: draftStartDate,
      endDate: draftEndDate,
      status: draftStatus,
      citizenSearch: draftCitizenSearch.trim(),
      titleSearch: draftTitleSearch.trim(),
      venueSearch: draftVenueSearch.trim(),
      search: draftSearch.trim(),
    });
  };

  const handleClear = () => {
    setDraftStartDate("");
    setDraftEndDate("");
    setDraftStatus("ALL");
    setDraftCitizenSearch("");
    setDraftTitleSearch("");
    setDraftVenueSearch("");
    setDraftSearch("");
    resetFilters();
  };

  const isAnyFilterApplied = Boolean(
    hasActiveFilters ||
      filters.startDate ||
      filters.endDate ||
      (filters.status && filters.status !== "ALL") ||
      filters.citizenSearch ||
      filters.titleSearch ||
      filters.venueSearch ||
      filters.search
  );

  // Export ALL matched records to official MTRCB Landscape PDF
  const handleExportPDF = () => {
    if (!filteredData || filteredData.length === 0) return;
    setIsExporting(true);
    setExportMessage(`Generating official PDF for all ${filteredData.length.toLocaleString()} public reports...`);

    setTimeout(() => {
      try {
        const doc = new jsPDF("landscape", "mm", "a4");

        // Header Title
        doc.setFontSize(14);
        doc.setTextColor(0, 114, 188);
        doc.text("MOVIE AND TELEVISION REVIEW AND CLASSIFICATION BOARD", 14, 15);
        doc.setFontSize(10);
        doc.setTextColor(71, 85, 105);
        doc.text("Public Citizen Reports Monitoring Directory", 14, 21);

        // Subheader metadata
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        const dateRangeText =
          filters.startDate || filters.endDate
            ? `Date Range: ${filters.startDate || "Beginning"} to ${filters.endDate || "Present"}`
            : "Date Range: All Dates";
        doc.text(
          `Generated: ${format(new Date(), "MMM dd, yyyy · hh:mm a")}  |  Status: ${filters.status || "ALL"}  |  ${dateRangeText}  |  Total Records: ${filteredData.length}`,
          14,
          27
        );

        const tableColumn = [
          "#",
          "ID",
          "Citizen Name",
          "Content Title",
          "Venue Name",
          "Event Date / Time",
          "Date Reported",
          "Status",
        ];

        const tableRows = filteredData.map((report: any, index: number) => [
          index + 1,
          String(report.id || "—"),
          `${report.user?.name || ""} ${report.user?.last_name || ""}`.trim() || "—",
          String(report.title || "—"),
          String(report.name || "—"),
          report.event_datetime
            ? format(new Date(report.event_datetime), "MMM dd, yyyy · hh:mm a")
            : "—",
          report.created_at
            ? format(new Date(report.created_at), "MMM dd, yyyy · hh:mm a")
            : "—",
          String(report.status || "PENDING"),
        ]);

        autoTable(doc, {
          head: [tableColumn],
          body: tableRows,
          startY: 32,
          theme: "grid",
          headStyles: {
            fillColor: [0, 114, 188],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 8,
          },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          styles: {
            fontSize: 7.5,
            cellPadding: 2,
            textColor: [15, 23, 42],
            overflow: "linebreak",
          },
          margin: { top: 12, left: 14, right: 14, bottom: 12 },
          didDrawPage: (data: any) => {
            const pageCount = (doc as any).internal.getNumberOfPages();
            doc.setFontSize(7.5);
            doc.setTextColor(148, 163, 184);
            doc.text(
              `Page ${data.pageNumber} of ${pageCount}`,
              doc.internal.pageSize.getWidth() - 30,
              doc.internal.pageSize.getHeight() - 6
            );
          },
        });

        doc.save(`MTRCB_Public_Reports_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
        Notify("success", `Exported ${filteredData.length} public reports to PDF.`);
      } catch (err: any) {
        console.error("PDF export error:", err);
        Notify("error", err?.message || "Failed to generate PDF export.");
      } finally {
        setIsExporting(false);
      }
    }, 100);
  };

  // Export ALL matched records to Excel spreadsheet
  const handleExportExcel = () => {
    if (!filteredData || filteredData.length === 0) return;
    setIsExporting(true);
    setExportMessage(`Generating Excel spreadsheet for all ${filteredData.length.toLocaleString()} public reports...`);

    setTimeout(() => {
      try {
        const rows = filteredData.map((item: any, idx: number) => ({
          "No.": idx + 1,
          "Report ID": item.id ?? "—",
          "Citizen Name": `${item.user?.name || ""} ${item.user?.last_name || ""}`.trim() || "—",
          "Citizen Email": item.user?.email || "—",
          "Citizen Contact": item.user?.phone_number || item.user?.contact_no || "—",
          "Content Title": item.title || "—",
          "Venue Name": item.name || "—",
          "Event Date": item.event_datetime
            ? format(new Date(item.event_datetime), "yyyy-MM-dd hh:mm a")
            : "—",
          "Date Reported": item.created_at
            ? format(new Date(item.created_at), "yyyy-MM-dd hh:mm a")
            : "—",
          Status: item.status || "PENDING",
          "Brief Description": item.report_description || "—",
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Public Reports");

        // Set column widths for readability
        ws["!cols"] = [
          { wch: 6 },  // No.
          { wch: 12 }, // Report ID
          { wch: 24 }, // Citizen Name
          { wch: 28 }, // Citizen Email
          { wch: 18 }, // Citizen Contact
          { wch: 30 }, // Content Title
          { wch: 24 }, // Venue Name
          { wch: 22 }, // Event Date
          { wch: 22 }, // Date Reported
          { wch: 16 }, // Status
          { wch: 50 }, // Brief Description
        ];

        XLSX.writeFile(wb, `MTRCB_Public_Reports_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
        Notify("success", `Exported ${filteredData.length} public reports to Excel.`);
      } catch (err: any) {
        console.error("Excel export error:", err);
        Notify("error", err?.message || "Failed to generate Excel export.");
      } finally {
        setIsExporting(false);
      }
    }, 100);
  };

  return (
    <>
      <DefaultPage
        title="Reports from the Public"
        subTitle="Manage and review content issue reports submitted by general citizens."
        badgeText="PUBLIC REPORTS"
        loading={isLoading}
      >
        <Box mb={2.5}>
          <Alert
            severity="info"
            sx={{
              borderRadius: "6px",
              fontSize: 13,
              border: "1px solid #BAE6FD",
              backgroundColor: "#F0F9FF",
              "& .MuiAlert-icon": {
                color: "#0072BC",
              },
            }}
          >
            Displays public reports submitted regarding movie contents, TV shows, and other public venues.
            Filter results and click <strong>Search</strong>. Your filters and pagination persist across views.
          </Alert>
        </Box>

        {isError && (
          <Box mb={2.5}>
            <Alert
              severity="error"
              sx={{
                borderRadius: "6px",
                fontSize: 13,
                border: "1px solid #FECACA",
              }}
            >
              Failed to load public reports.
            </Alert>
          </Box>
        )}

        {/* HRIS-MVP Gold Standard Multi-Field Filter Bar */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E2E8F0",
            borderRadius: "6px",
            p: 2,
            mb: 2,
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
          }}
        >
          <Stack spacing={2}>
            {/* Input fields row */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <TextField
                label="Event Date From"
                type="date"
                size="small"
                value={draftStartDate}
                onChange={(e) => setDraftStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 155,
                  "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                }}
              />

              <TextField
                label="Event Date To"
                type="date"
                size="small"
                value={draftEndDate}
                onChange={(e) => setDraftEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 155,
                  "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                }}
              />

              <TextField
                select
                size="small"
                label="Status"
                value={draftStatus}
                onChange={(e) => setDraftStatus(e.target.value)}
                sx={{
                  minWidth: 160,
                  "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                }}
              >
                <MenuItem value="ALL">ALL STATUSES</MenuItem>
                <MenuItem value="PENDING">PENDING</MenuItem>
                <MenuItem value="ACKNOWLEDGED">ACKNOWLEDGED</MenuItem>
                <MenuItem value="DUPLICATE">DUPLICATE</MenuItem>
                <MenuItem value="REJECTED">REJECTED</MenuItem>
              </TextField>

              <TextField
                size="small"
                label="Citizen Name"
                placeholder="Search citizen..."
                value={draftCitizenSearch}
                onChange={(e) => setDraftCitizenSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                sx={{
                  minWidth: 175,
                  "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                }}
              />

              <TextField
                size="small"
                label="Content Title"
                placeholder="Search title..."
                value={draftTitleSearch}
                onChange={(e) => setDraftTitleSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                sx={{
                  minWidth: 175,
                  "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                }}
              />

              <TextField
                size="small"
                label="Venue Name"
                placeholder="Search venue..."
                value={draftVenueSearch}
                onChange={(e) => setDraftVenueSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                sx={{
                  minWidth: 175,
                  "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                }}
              />

              <TextField
                size="small"
                label="Keyword / Report ID"
                placeholder="Search all..."
                value={draftSearch}
                onChange={(e) => setDraftSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                sx={{
                  minWidth: 180,
                  "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                }}
              />
            </Stack>

            <Divider sx={{ borderColor: "#F1F5F9" }} />

            {/* Actions row: Search & Clear on left, Exports on right */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1.5}
            >
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SearchRounded fontSize="small" />}
                  onClick={handleSearch}
                  disabled={isFetching}
                  sx={{
                    backgroundColor: "#0072BC",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    fontSize: 13,
                    px: 2,
                    py: 0.75,
                    borderRadius: "4px",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#005a96",
                      boxShadow: "none",
                    },
                  }}
                >
                  {isFetching ? "Searching..." : "Search"}
                </Button>

                {isAnyFilterApplied && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RestartAltRounded fontSize="small" />}
                    onClick={handleClear}
                    disabled={isFetching}
                    sx={{
                      color: "#64748B",
                      borderColor: "#CBD5E1",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 13,
                      borderRadius: "4px",
                      "&:hover": {
                        borderColor: "#94A3B8",
                        backgroundColor: "#F8FAFC",
                      },
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfRounded fontSize="small" />}
                  onClick={handleExportPDF}
                  disabled={isExporting || !filteredData || filteredData.length === 0}
                  sx={{
                    color: "#334155",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Export PDF
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TableChartRounded fontSize="small" />}
                  onClick={handleExportExcel}
                  disabled={isExporting || !filteredData || filteredData.length === 0}
                  sx={{
                    color: "#0072BC",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#0072BC",
                      backgroundColor: "#F0F9FF",
                    },
                  }}
                >
                  Export Excel
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>

        <Box mb={2}>
          <Alert
            severity="success"
            sx={{
              borderRadius: "6px",
              fontSize: 13,
              border: "1px solid #BBF7D0",
              backgroundColor: "#F0FDF4",
              py: 0.5,
              "& .MuiAlert-icon": {
                color: "#00934C",
              },
            }}
          >
            Total matched records:{" "}
            <strong>{Number(filteredData?.length || 0).toLocaleString()}</strong>
          </Alert>
        </Box>

        <DataTableFive<any>
          data={paginatedData}
          total={filteredData?.length || 0}
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => {
            setPage(Math.max(newPage, 1));
          }}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={isLoading}
          isFetching={isFetching}
          onRowClick={(row: any) => handleViewDetails(row.original)}
        />
      </DefaultPage>

      {/* Full-Screen Loading Backdrop for Exporting: Disables platform and prevents interaction */}
      <Backdrop
        open={isExporting}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 9999,
          color: "#FFFFFF",
          backgroundColor: "rgba(15, 23, 42, 0.75)",
          backdropFilter: "blur(4px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={54} thickness={4} />
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#FFFFFF" }}>
          Generating Export...
        </Typography>
        <Typography sx={{ fontSize: 14, color: "rgba(255, 255, 255, 0.85)" }}>
          {exportMessage ||
            `Processing ${filteredData.length.toLocaleString()} public report records. Please wait.`}
        </Typography>
      </Backdrop>
    </>
  );
}
